// Developer: Nithin | Date: 2026-07-26 | Added + Shift button and ShiftModal for employee shift management
import React, { useState } from "react";
import { Plus, Search, Users, UserCheck, AlertCircle, UserMinus, Filter, X, Upload, Download } from "lucide-react";
import { useEmployees } from "../hooks/useEmployees";
import { EmployeeTable } from "../components/onboarding/EmployeeTable";
import { EmployeeFormModal } from "../components/onboarding/EmployeeFormModal";
import { EmployeeDetailModal } from "../components/onboarding/EmployeeDetailModal";
import { StatusConfirmModal } from "../components/StatusConfirmModal";
import { PaySalaryModal } from "../components/onboarding/PaySalaryModal";
import { ShiftModal } from "../components/onboarding/ShiftModal";
import StatCard from "@/components/ui/StatCard";
import { Pagination } from "@/components/ui";
import { DEPARTMENTS, EMPLOYMENT_STATUS, EMPLOYMENT_STATUS_LABELS, EMPLOYMENT_TYPE } from "../constants/hrConstants";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";

export function EmployeeOnboardingScreen() {
  const {
    employees, stats, search, setSearch,
    filterDept, setFilterDept, filterStatus, setFilterStatus,
    filterType, setFilterType,
    isFormOpen, editingEmployee, viewingEmployee, setViewingEmployee,
    formData, formErrors, isSaving,
    openCreate, openEdit, closeForm, updateField, save,
    addEmployee, toggleEmployeeStatus,
  } = useEmployees();

  const [statusConfirmTarget, setStatusConfirmTarget] = useState(null); // holds entire employee object
  const [paySalaryTarget, setPaySalaryTarget] = useState(null); // holds entire employee object for payment
  const [searchVisible, setSearchVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [isShiftOpen, setIsShiftOpen] = useState(false); // controls + Shift modal visibility

  React.useEffect(() => {
    setPage(1);
  }, [search, filterDept, filterStatus, filterType]);

  const PAGE_SIZE = 10;
  const totalPages = Math.ceil(employees.length / PAGE_SIZE) || 1;
  const paginatedEmployees = employees.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleToggleStatusClick = (id) => {
    const emp = employees.find((e) => e._id === id || e.id === id);
    if (emp) {
      setStatusConfirmTarget(emp);
    }
  };

  const executeStatusToggle = () => {
    if (statusConfirmTarget) {
      toggleEmployeeStatus(statusConfirmTarget.id);
      setStatusConfirmTarget(null);
    }
  };

  const handleEmployeeSpreadsheet = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async ({ target }) => {
      try {
        const sheet = XLSX.read(target.result, { type: "array" }).Sheets.Sheet1 || XLSX.read(target.result, { type: "array" }).Sheets[XLSX.read(target.result, { type: "array" }).SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        let created = 0;
        for (const row of rows) {
          const required = ["First Name", "Last Name", "Email", "Phone", "Employee ID", "Password", "Department", "Role", "Joining Date", "Work Location"];
          if (required.some((key) => !String(row[key] || "").trim())) continue;
          const payload = new FormData();
          const fields = {
            firstName: row["First Name"], lastName: row["Last Name"], email: row.Email, phone: String(row.Phone),
            employeeId: String(row["Employee ID"]), password: row.Password, department: row.Department, role: row.Role,
            joiningDate: row["Joining Date"], workLocation: row["Work Location"], designation: row.Designation || row.Role,
            employmentType: row["Employment Type"] || "full_time", salary: row.Salary || 0,
            reportingManager: row["Reporting Manager"], notes: row.Notes, companyName: "Verve Kitchens",
          };
          Object.entries(fields).forEach(([key, value]) => { if (value !== "" && value !== undefined) payload.append(key, value); });
          await addEmployee(payload);
          created += 1;
        }
        toast.success(`${created} employee${created === 1 ? "" : "s"} uploaded`);
      } catch (error) {
        console.error(error);
        toast.error("Employee upload failed. Use the supplied template.");
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = "";
  };

  const downloadEmployeeTemplate = () => {
    const sheet = XLSX.utils.aoa_to_sheet([["Employee ID", "First Name", "Last Name", "Email", "Phone", "Password", "Designation", "Department", "Role", "Employment Type", "Joining Date", "Salary", "Work Location", "Reporting Manager", "Notes"]]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Employees"); XLSX.writeFile(workbook, "employee-upload-template.xlsx");
  };

  return (
    <>
      <div className="space-y-6 animate-in fade-in duration-400 bg-slate-50">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard variant="luxury" title="Total Employees" value={stats.total} topColor="border-t-blue-500" iconHoverColor="group-hover:text-blue-500" illustration={<Users className="w-10 h-10" strokeWidth={1.5} />} />
        <StatCard variant="luxury" title="Active" value={stats.active} topColor="border-t-emerald-500" iconHoverColor="group-hover:text-emerald-500" illustration={<UserCheck className="w-10 h-10" strokeWidth={1.5} />} />
        <StatCard variant="luxury" title="On Probation" value={stats.probation} topColor="border-t-amber-500" iconHoverColor="group-hover:text-amber-500" illustration={<AlertCircle className="w-10 h-10" strokeWidth={1.5} />} />
        <StatCard variant="luxury" title="Inactive" value={stats.inactive} topColor="border-t-slate-500" iconHoverColor="group-hover:text-slate-600" illustration={<UserMinus className="w-10 h-10" strokeWidth={1.5} />} />
      </div>

      {/* Control Panel */}
      <div className="card p-2 sm:p-3 flex flex-wrap items-center gap-2 bg-white shadow-sm border border-slate-100 animate-in fade-in slide-in-from-top-1 rounded-md">

        {/* Search Toggle Button */}
        <button
          onClick={() => { setSearchVisible(v => !v); if (searchVisible) setSearch(''); }}
          className={`p-2 h-9 w-9 flex items-center justify-center rounded-md border transition-all ${
            searchVisible
              ? 'bg-blue-600 border-blue-600 text-white shadow-sm shadow-blue-200'
              : 'border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-blue-600 hover:border-blue-300'
          }`}
          title="Search"
        >
          <Search size={16} />
        </button>

        {/* Dept Dropdown */}
        <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0">
          <select
            className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 text-slate-600 min-w-[140px] max-w-[180px] w-auto"
            value={filterDept} onChange={(e) => setFilterDept(e.target.value)}
          >
            <option value="">All Departments</option>
            {DEPARTMENTS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>

        {/* Status Dropdown */}
        <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0 hidden sm:block">
          <select
            className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 text-slate-600 min-w-[120px] max-w-[160px] w-auto"
            value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Status</option>
            {Object.entries(EMPLOYMENT_STATUS).map(([, v]) => (
              <option key={v} value={v}>{EMPLOYMENT_STATUS_LABELS[v]}</option>
            ))}
          </select>
        </div>

        {/* Type Dropdown */}
        <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0 hidden sm:block">
          <select
            className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 text-slate-600 min-w-[120px] max-w-[160px] w-auto"
            value={filterType} onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">All Types</option>
            {Object.entries(EMPLOYMENT_TYPE).map(([, v]) => (
              <option key={v} value={v}>{v.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}</option>
            ))}
          </select>
        </div>

        {/* Clear Filters Button */}
        {(filterDept || filterStatus || filterType) && (
          <button
            onClick={() => {
              setFilterDept("");
              setFilterStatus("");
              setFilterType("");
            }}
            className="h-9 flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-blue-600 transition-all whitespace-nowrap"
            title="Clear all filters"
          >
            <Filter size={15} /> Clear Filters
          </button>
        )}

          <button
            onClick={downloadEmployeeTemplate}
            className="h-9 flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-blue-600 transition-all whitespace-nowrap"
            title="Download employee upload template"
          >
            <Download size={15} /> Template
          </button>
          <button
            onClick={() => document.getElementById("employee-spreadsheet-file-input").click()}
            className="h-9 flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-blue-600 transition-all whitespace-nowrap"
            title="Upload employee spreadsheet"
          >
            <Upload size={15} /> Upload
          </button>
          <input id="employee-spreadsheet-file-input" type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleEmployeeSpreadsheet} />
        {/* Add Employee + Shift Buttons */}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setIsShiftOpen(true)}
            className="h-9 flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 hover:border-blue-400 shadow-sm hover:scale-105 active:scale-95 transition-all whitespace-nowrap"
          >
            <Plus size={16} /> Shift
          </button>
          <button
            onClick={openCreate}
            className="h-9 flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-sm shadow-blue-200 hover:scale-105 active:scale-95 transition-all whitespace-nowrap"
          >
            <Plus size={16} /> Add Employee
          </button>
        </div>
      </div>

      {/* Search Overlay */}
      {searchVisible && (
        <div className="card p-3 flex items-center gap-3 bg-white shadow-sm border border-slate-100 rounded-md animate-in fade-in slide-in-from-top-1">
          <Search size={16} className="text-slate-400 shrink-0" />
          <input
            type="text"
            autoFocus
            className="flex-1 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 outline-none"
            placeholder="Search by name, email, ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-slate-400 hover:text-slate-600">
              <X size={15} />
            </button>
          )}
        </div>
      )}

      {/* Table & Pagination Wrapper */}
      <div className="flex flex-col">
        <EmployeeTable
          employees={paginatedEmployees}
          onEdit={openEdit}
          onView={setViewingEmployee}
          onToggleStatus={handleToggleStatusClick}
          onPay={setPaySalaryTarget}
        />
        {totalPages > 1 && (
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        )}
      </div>
      </div>

      {/* Modals */}
      <EmployeeFormModal
        isOpen={isFormOpen}
        onClose={closeForm}
        formData={formData}
        formErrors={formErrors}
        onFieldChange={updateField}
        onSubmit={save}
        isEditing={Boolean(editingEmployee)}
        isSaving={isSaving}
      />
      <EmployeeDetailModal
        employee={viewingEmployee}
        onClose={() => setViewingEmployee(null)}
      />
      <StatusConfirmModal
        isOpen={Boolean(statusConfirmTarget)}
        onClose={() => setStatusConfirmTarget(null)}
        onConfirm={executeStatusToggle}
        employeeName={statusConfirmTarget?.name}
        currentStatus={statusConfirmTarget?.status}
      />
      <PaySalaryModal
        isOpen={Boolean(paySalaryTarget)}
        onClose={() => setPaySalaryTarget(null)}
        employee={paySalaryTarget}
      />
      <ShiftModal
        isOpen={isShiftOpen}
        onClose={() => setIsShiftOpen(false)}
      />
    </>
  );
}
